<template>
	<div>
		<!-- Show list view when filter is active -->
		<div v-if="isFilterActive" class="account-list">
			<!-- Current Accounts -->
			<template v-if="showCurrentCard && currentAccounts.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Current accounts" :rightLabel="getCurrentAccountsBalance()" class="section-header" />
				<AccountListItem
					v-for="account in currentAccounts"
					:key="`current-${account.id}`"
					:provider-name="account.bank?.name || 'Bank'"
					:line1="account.accountType?.prettyName || 'Current'"
					:line2="account.accountNumber || ''"
					:value="this.formatter.format(account.balance)"
					label="Balance"
					:avatar-url="account.bank?.logoUri || require('@/assets/round1.png')"
					@click="handleAccountListItemClick(account, 'current')"
				/>
			</template>

			<!-- Credit Accounts -->
			<template v-if="showCreditCard && creditAccounts.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Credit Cards" :rightLabel="getCreditCardsBalance()" class="section-header" />
				<AccountListItem
					v-for="account in creditAccounts"
					:key="`credit-${account.id}`"
					:provider-name="account.bank?.name || 'Bank'"
					:line1="account.accountType?.prettyName || 'Credit'"
					:line2="account.accountNumber || ''"
					:value="this.formatter.format(account.balance)"
					label="Balance"
					:avatar-url="account.bank?.logoUri || require('@/assets/round1.png')"
					@click="handleAccountListItemClick(account, 'credit')"
				/>
			</template>

			<!-- Savings Accounts -->
			<template v-if="showSavingCard && savingAccounts.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Savings accounts" :rightLabel="getSavingAccountsBalance()" class="section-header" />
				<AccountListItem
					v-for="account in savingAccounts"
					:key="`savings-${account.id}`"
					:provider-name="account.bank?.name || 'Bank'"
					:line1="account.accountType?.prettyName || 'Savings'"
					:line2="account.accountNumber || ''"
					:value="this.formatter.format(account.balance)"
					label="Balance"
					:avatar-url="account.bank?.logoUri || require('@/assets/round1.png')"
					@click="handleAccountListItemClick(account, 'savings')"
				/>
			</template>

			<!-- Mortgage Accounts -->
			<template v-if="showMortgageCard && mortgageInfo && mortgageInfo.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Mortgages" class="section-header" />
				<AccountListItem
					v-for="account in mortgageInfo"
					:key="`mortgage-${account.id}`"
					:provider-name="account.account?.provider?.name || 'Provider'"
					:line1="account.policyType?.name || 'Mortgage'"
					:line2="account.account?.reference || ''"
					:value="this.formatter.format(account.account?.balance?.amount || 0)"
					label="Balance"
					:avatar-url="account.account?.provider?.logoUri || require('@/assets/round1.png')"
					@click="handleAccountListItemClick(account, 'mortgage')"
				/>
			</template>

			<!-- Loans (Credit Info) -->
			<template v-if="showCreditInfo && creditInfo && creditInfo.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Loans" class="section-header" />
				<AccountListItem
					v-for="account in creditInfo"
					:key="`loan-${account.id}`"
					:provider-name="account.provider?.name || 'Provider'"
					:line1="account.creditType?.name || 'Loan'"
					:line2="account.reference || ''"
					:value="this.formatter.format(account.balance?.amount || 0)"
					label="Balance"
					:avatar-url="account.provider?.logoUri || require('@/assets/round1.png')"
					@click="handleAccountListItemClick(account, 'loan')"
				/>
			</template>

			<!-- Pensions (investmentInfo filtered by accountType.name === 'Pension') -->
			<template v-if="showPensionCard && pensionAccounts && pensionAccounts.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Pensions" class="section-header" />
				<AccountListItem
					v-for="account in pensionAccounts"
						:key="`pension-${account.id}`"
						:provider-name="account.provider?.name || 'Provider'"
						:line1="account.investmentType?.name || 'Pension'"
						:line2="account.reference || ''"
						:value="typeof account.balance === 'number' ? this.formatter.format(account.balance) : this.formatter.format(0)"
						label="Balance"
						:avatar-url="account.provider?.logoUri || require('@/assets/round1.png')"
						@click="handleAccountListItemClick(account, 'pension')"
					/>
			</template>

			<!-- Investments (investmentInfo filtered by accountType.name === 'Investment') -->
			<template v-if="showInvestmentCard && investmentAccounts && investmentAccounts.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Investments" class="section-header" />
				<AccountListItem
					v-for="account in investmentAccounts"
					:key="`investment-${account.id}`"
					:provider-name="account.provider?.name || 'Provider'"
					:line1="account.investmentType?.name || 'Investment'"
					:line2="account.reference || ''"
					:value="typeof account.balance === 'number' ? this.formatter.format(account.balance) : this.formatter.format(0)"
					label="Balance"
					:avatar-url="account.provider?.logoUri || require('@/assets/round1.png')"
					@click="handleAccountListItemClick(account, 'investment')"
				/>
			</template>

			<!-- Additional Long-term Documents -->
			<template v-if="(selectedSection === 'all' || selectedSection === 'lt') && filteredAdditionalLongTerm && filteredAdditionalLongTerm.length > 0">
				<AccountListItem
					v-for="doc in filteredAdditionalLongTerm"
					:key="`doc-lt-${doc.id}`"
					:provider-name="doc.company || 'Document'"
					:line1="doc.documentName"
					:value="doc.price ? this.formatter.format(parseFloat(String(doc.price).replace(/,/g, ''))) : ''"
					:label="doc.price ? 'Value' : ''"
					:avatar-url="getDocumentIcon(doc)"
					@click="handleDocumentClick(doc)"
				/>
			</template>

			<!-- Insurance Accounts -->
			<template v-if="showInsuranceCard && insuranceInfo && insuranceInfo.length > 0">
				<SectionHeader v-if="selectedSubSection !== 'all'" title="Insurance Policies" class="section-header" />
				<AccountListItem
					v-for="account in insuranceInfo"
					:key="`insurance-${account.id}`"
					:provider-name="account.provider?.name || 'Provider'"
					:line1="account.policyType?.name || 'Insurance'"
					:line2="account.policyNumber || ''"
					:value="account.endDateString"
					label="Renewal"
					:avatar-url="account.provider?.logoUri || require('@/assets/round1.png')"
					@click="handleAccountListItemClick(account, 'policy')"
				/>
			</template>

			<!-- Additional Insurance Documents -->
			<template v-if="(selectedSection === 'all' || selectedSection === 'ins') && additionalInsurance && additionalInsurance.length > 0">
				<AccountListItem
					v-for="doc in additionalInsurance"
					:key="`doc-ins-${doc.id}`"
					:provider-name="doc.company || 'Document'"
					:line1="doc.documentName"
					:line2="doc.sysoDocumentType?.name || ''"
					:value="doc.renewalDateString || ''"
					label="Renewal"
					:avatar-url="getDocumentIcon(doc)"
					@click="handleDocumentClick(doc)"
				/>
			</template>

			<!-- Empty State -->
			<div v-if="showEmptyState" class="empty-state">
				<p class="empty-state-text">{{ emptyStateHeadline }}</p>
				<p class="empty-state-subtext">{{ emptyStateSubtext }}</p>
				<MGButton
					:fullWidth="true"
					:noCaps="true"
					:stacked="true"
					:closerStack="true"
					:text="emptyStateButtonText"
					:plus="true"
					@click="handleAddAccountClick"
				/>
			</div>

			<MGButton
			v-if="selectedSection === 'd2d' && currentAccounts.length > 0 && !showEmptyState"
				:fullWidth="true"
				:noCaps="true"
				text="See spending analysis"
				:outline="true"
				:arrow="true"
				@click="handleSpendingAnalysisClick"
				:icon="require('@/assets/spending-analysis.png')"
				leftIconSize="16"
			/>
			<MGButton
			v-if="(selectedSection === 'd2d' || selectedSection === 'lt' || selectedSection === 'ins') && !showEmptyState"
				class="add-account-button"
				:fullWidth="true"
				:noCaps="true"
				:stacked="true"
				:closerStack="true"
				:text="emptyStateButtonText"
				:plus="true"
				@click="handleAddAccountClick"
			/>

		</div>

		<!-- Show card view when filter is not active -->
		<div v-else>
			<!-- Single grid when 5 or fewer cards -->
			<div v-if="!showSectionHeaders" class="account-cards-grid">
				<AccountCard
					v-if="currentAccounts.length > 0 && showCurrentCard"
					key="current-account"
					label="Current"
					:icon="getCurrentAccountIcon()"
					:accountIcons="getAllCurrentAccountIcons()"
					:balance="formatter.format(currentAccountsBalance)"
					@click="handleCurrentAccountClick"
				/>

				<AccountCard
					v-if="creditAccounts.length > 0 && showCreditCard"
					key="credit-account"
					label="Credit Card"
					:icon="getCreditCardIcon()"
					:accountIcons="getAllCreditCardIcons()"
					:balance="formatter.format(creditAccountsBalance)"
					@click="handleCreditAccountClick"
				/>

				<AccountCard
					v-if="savingAccounts.length > 0 && showSavingCard"
					key="savings-account"
					label="Savings"
					:icon="getSavingsAccountIcon()"
					:accountIcons="getAllSavingsAccountIcons()"
					:balance="formatter.format(savingAccountsBalance)"
					@click="handleSavingAccountClick"
				/>

				<AccountCard
					v-if="((mortgageInfo && mortgageInfo.length > 0) || (additionalMortgageDocs && additionalMortgageDocs.length > 0)) && showMortgageCard"
					key="mortgage"
					label="Mortgage"
					:icon="getMortgageIcon()"
					:accountIcons="getAllMortgageAccountIcons()"
					:balance="formatter.format(mortgageBalance)"
					@click="handleMortgageClick"
				/>

				<AccountCard
					v-if="((insuranceInfo && insuranceInfo.length > 0) || (additionalInsurance && additionalInsurance.length > 0)) && showInsuranceCard"
					key="insurance"
					label="Insurance Policies"
					:icon="getInsuranceIcon()"
					:accountIcons="getAllInsuranceAccountIcons()"
					:b-alance="formatter.format(insuranceBalance)"
					@click="handleInsuranceClick"
				/>

				<AccountCard
					v-if="creditInfo && creditInfo.length > 0 && showCreditInfo"
					key="credit"
					label="Loans"
					:icon="getCreditInfoIcon()"
					:accountIcons="getAllCreditInfoIcons()"
					:balance="formatter.format(loanBalance)"
					@click="handleCreditClick"
				/>

				<!-- pension -->
				<AccountCard
					v-if="((pensionAccounts && pensionAccounts.length > 0) || (additionalPensionDocs && additionalPensionDocs.length > 0)) && showPensionCard"
					key="pension"
					label="Pension"
					:icon="require('@/assets/accountTypes/pension.png')"
					:accountIcons="getAllPensionAccountIcons()"
					:balance="formatter.format(pensionBalance)"
					@click="handlePensionClick"
				/>

				<!-- investment -->
				<AccountCard
					v-if="investmentAccounts && investmentAccounts.length > 0 && showInvestmentCard"
					key="investment"
					label="Investments"
					:balance="formatter.format(investmentBalance)"
					:icon="require('@/assets/accountTypes/investments.png')"
					:accountIcons="getAllInvestmentAccountIcons()"
					@click="handleInvestmentClick"
				/>

			</div>

			<!-- Sectioned cards with headers when 5+ cards -->
			<div v-else>
				<!-- Day-to-day Section -->
				<template v-if="(currentAccounts.length > 0 && showCurrentCard) || (creditAccounts.length > 0 && showCreditCard)">
					<SectionHeader
						title="Day-to-day"
						class="section-header"
						@action="navigateToDayToDay"
					/>
					<div class="account-cards-grid">
						<AccountCard
							v-if="currentAccounts.length > 0 && showCurrentCard"
							key="current-account"
							label="Current accounts"
							:icon="getCurrentAccountIcon()"
							:accountIcons="getAllCurrentAccountIcons()"
							:balance="formatter.format(currentAccountsBalance)"
							@click="handleCurrentAccountClick"
						/>

						<AccountCard
							v-if="creditAccounts.length > 0 && showCreditCard"
							key="credit-account"
							label="Credit cards"
							:icon="getCreditCardIcon()"
							:accountIcons="getAllCreditCardIcons()"
							:balance="formatter.format(creditAccountsBalance)"
							@click="handleCreditAccountClick"
						/>

						<AccountCard
							v-if="savingAccounts.length > 0 && showSavingCard"
							key="savings-account"
							label="Savings"
							:icon="getSavingsAccountIcon()"
							:accountIcons="getAllSavingsAccountIcons()"
							:balance="formatter.format(savingAccountsBalance)"
							@click="handleSavingAccountClick"
						/>					
					</div>
				</template>

				<!-- Long-term Section -->
				<template v-if="((mortgageInfo && mortgageInfo.length > 0) || (additionalMortgageDocs && additionalMortgageDocs.length > 0)) && showMortgageCard">
					<SectionHeader
						title="Long-term"
						class="section-header"
						@action="navigateToLongTerm"
					/>
					<div class="account-cards-grid">
						<AccountCard
							key="mortgage"
							label="Mortgages"
							:icon="getMortgageIcon()"
							:accountIcons="getAllMortgageAccountIcons()"
							:balance="formatter.format(mortgageBalance)"
							@click="handleMortgageClick"
						/>

						<AccountCard
							v-if="creditInfo && creditInfo.length > 0 && showCreditInfo"
							key="credit"
							label="Loans"
							:icon="getCreditInfoIcon()"
							:accountIcons="getAllCreditInfoIcons()"
							:balance="formatter.format(loanBalance)"
							@click="handleCreditClick"
						/>

						<!-- pension -->
						<AccountCard
							v-if="((pensionAccounts && pensionAccounts.length > 0) || (additionalPensionDocs && additionalPensionDocs.length > 0)) && showPensionCard"
							key="pension"
							label="Pensions"
							:icon="require('@/assets/accountTypes/pension.png')"
							:accountIcons="getAllPensionAccountIcons()"
							:balance="formatter.format(pensionBalance)"
							@click="handlePensionClick"
						/>

						<!-- investment -->
						<AccountCard
							v-if="investmentAccounts && investmentAccounts.length > 0 && showInvestmentCard"
							key="investment"
							label="Investments"
							:icon="require('@/assets/accountTypes/investments.png')"
							:accountIcons="getAllInvestmentAccountIcons()"
							:balance="formatter.format(investmentBalance)"
							@click="handleInvestmentClick"
						/>
					</div>
				</template>

				<!-- Insurance Section -->
				<template v-if="((insuranceInfo && insuranceInfo.length > 0) || (additionalInsurance && additionalInsurance.length > 0)) && showInsuranceCard">
					<SectionHeader
						title="Insurance"
						class="section-header"
						@action="navigateToInsurance"
					/>
					<div class="account-cards-grid">
						<AccountCard
							key="insurance"
							label="Insurance Policies"
							:icon="getInsuranceIcon()"
							:accountIcons="getAllInsuranceAccountIcons()"
							:b-alance="formatter.format(insuranceBalance)"
							@click="handleInsuranceClick"
						/>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import AccountCard from './AccountCard.vue';
import AccountListItem from './AccountListItem.vue';
import MGButton from './MGButton.vue';
import SectionHeader from './SectionHeader.vue';

export default {
	name: 'AccountCardsSection',
	emits: ['add-account-click', 'filter-changed'],
	components: {
		AccountCard,
		AccountListItem,
		SectionHeader,
		MGButton
	},
	props: {
		currentAccounts: {
			type: Array,
			default: () => []
		},
		currentAccountsBalance: {
			type: Number,
			default: 0
		},
		creditAccounts: {
			type: Array,
			default: () => []
		},
		creditAccountsBalance: {
			type: Number,
			default: 0
		},
		savingAccounts : {
			type: Array,
			default: () => []
		},
		savingAccountsBalance: {
			type: Number,
			default: 0
		},
		mortgageInfo: {
			type: Array,
			default: () => []
		},
		insuranceInfo: {
			type: Array,
			default: () => []
		},
		creditInfo: {
			type: Array,
			default: () => []
		},
		investmentInfo: {
			type: Array,
			default: () => []
		},
		additionalLongTerm: {
			type: Array,
			default: () => []
		},
		additionalInsurance: {
			type: Array,
			default: () => []
		},
		showCurrentCard: {
			type: Boolean,
			default: false
		},
		showCreditCard: {
			type: Boolean,
			default: false
		},
		showSavingCard: {
			type: Boolean,
			default: false
		},
		showMortgageCard: {
			type: Boolean,
			default: false
		},
		showPensionCard: {
			type: Boolean,
			default: false
		},
		showInvestmentCard: {
			type: Boolean,
			default: false
		},
		showInsuranceCard: {
			type: Boolean,
			default: false
		},
		showCreditInfo: {
			type: Boolean,
			default: false
		},
		formatter: {
			type: Object,
			required: true
		},
		selectedSection: {
			type: String,
			default: 'all'
		},
		selectedSubSection: {
			type: String,
			default: 'all'
		}
	},
	computed: {
		totalVisibleCards() {
			let count = 0;
			if (this.currentAccounts.length > 0 && this.showCurrentCard) count++;
			if (this.creditAccounts.length > 0 && this.showCreditCard) count++;
			if (((this.insuranceInfo && this.insuranceInfo.length > 0) || (this.additionalInsurance && this.additionalInsurance.length > 0)) && this.showInsuranceCard) count++;
			if (((this.mortgageInfo && this.mortgageInfo.length > 0) || (this.additionalMortgageDocs && this.additionalMortgageDocs.length > 0)) && this.showMortgageCard) count++;
			if (this.savingAccounts.length > 0 && this.showSavingCard) count++;
			if (this.creditInfo && this.creditInfo.length > 0 && this.showCreditInfo) count++;
			if (((this.pensionAccounts && this.pensionAccounts.length > 0) || (this.additionalPensionDocs && this.additionalPensionDocs.length > 0)) && this.showPensionCard) count++;
			return count;
		},
		showSectionHeaders() {
			return this.totalVisibleCards >= 5;
		},
		isFilterActive() {
			return this.selectedSection !== 'all';
		},
		pensionAccounts() {
			if (!this.investmentInfo) return [];
			return this.investmentInfo.filter(account => account.accountType?.name === 'Pension');
		},
		investmentAccounts() {
			if (!this.investmentInfo) return [];
			return this.investmentInfo.filter(account => account.accountType?.name === 'Investment');
		},
		additionalPensionDocs() {
			if (!this.additionalLongTerm) return [];
			return this.additionalLongTerm.filter(doc => doc.sysoDocumentType?.name === 'Pension');
		},
		additionalMortgageDocs() {
			if (!this.additionalLongTerm) return [];
			return this.additionalLongTerm.filter(doc => doc.sysoDocumentType?.name === 'Mortgage');
		},
		additionalInvestmentDocs() {
			if (!this.additionalLongTerm) return [];
			return this.additionalLongTerm.filter(doc => doc.sysoDocumentType?.name === 'Investment');
		},
		additionalLoanDocs() {
			if (!this.additionalLongTerm) return [];
			return this.additionalLongTerm.filter(doc => doc.sysoDocumentType?.name === 'Loan' || doc.sysoDocumentType?.name === 'Credit');
		},
		filteredAdditionalLongTerm() {
			if (!this.additionalLongTerm) return [];
			if (this.selectedSubSection === 'all') return this.additionalLongTerm;
			if (this.selectedSubSection === 'pensions') return this.additionalPensionDocs;
			if (this.selectedSubSection === 'mortgages') return this.additionalMortgageDocs;
			if (this.selectedSubSection === 'investments') return this.additionalInvestmentDocs;
			if (this.selectedSubSection === 'loans') return this.additionalLoanDocs;
			return this.additionalLongTerm;
		},
		mortgageBalance() {
			if (!this.mortgageInfo || this.mortgageInfo.length === 0) return 0;
			return this.mortgageInfo.reduce((sum, mortgage) => {
				return sum + (mortgage.account?.balance?.amount || 0);
			}, 0);
		},
		loanBalance() {
			if (!this.creditInfo || this.creditInfo.length === 0) return 0;
			return this.creditInfo.reduce((sum, loan) => {
				return sum + (loan.balance?.amount || 0);
			}, 0);
		},
		pensionBalance() {
			let total = 0;
			if (this.pensionAccounts && this.pensionAccounts.length > 0) {
				total += this.pensionAccounts.reduce((sum, account) => {
					return sum + (account.balance || 0);
				}, 0);
			}
			if (this.additionalPensionDocs && this.additionalPensionDocs.length > 0) {
				total += this.additionalPensionDocs.reduce((sum, doc) => {
					const price = doc.price ? parseFloat(String(doc.price).replace(/,/g, '')) : 0;
					return sum + (isNaN(price) ? 0 : price);
				}, 0);
			}
			return total;
		},
		investmentBalance() {
			if (!this.investmentAccounts || this.investmentAccounts.length === 0) return 0;
			return this.investmentAccounts.reduce((sum, account) => {
				return sum + (account.balance || 0);
			}, 0);
		},
		insuranceBalance() {
			if (!this.insuranceInfo || this.insuranceInfo.length === 0) return 0;
			return this.insuranceInfo.reduce((sum, policy) => {
				const itemsSum = (policy.insuredItems || []).reduce((itemSum, item) => {
					return itemSum + (item.valueInsured || 0);
				}, 0);
				return sum + itemsSum;
			}, 0);
		},
		isDayToDayEmpty() {
			return this.currentAccounts.length === 0 &&
				this.creditAccounts.length === 0 &&
				this.savingAccounts.length === 0;
		},
		isLongTermEmpty() {
			return (!this.mortgageInfo || this.mortgageInfo.length === 0) &&
				(!this.creditInfo || this.creditInfo.length === 0) &&
				(!this.pensionAccounts || this.pensionAccounts.length === 0) &&
				(!this.investmentAccounts || this.investmentAccounts.length === 0) &&
				(!this.additionalLongTerm || this.additionalLongTerm.length === 0);
		},
		isInsuranceEmpty() {
			return (!this.insuranceInfo || this.insuranceInfo.length === 0) &&
				(!this.additionalInsurance || this.additionalInsurance.length === 0);
		},
		isCurrentSubSectionEmpty() {
			if (this.selectedSubSection === 'all') return false;
			// d2d sub-sections
			if (this.selectedSubSection === 'current') return this.currentAccounts.length === 0;
			if (this.selectedSubSection === 'credit') return this.creditAccounts.length === 0;
			if (this.selectedSubSection === 'savings') return this.savingAccounts.length === 0;
			// lt sub-sections
			if (this.selectedSubSection === 'mortgages') return !this.mortgageInfo || this.mortgageInfo.length === 0;
			if (this.selectedSubSection === 'pensions') return !this.pensionAccounts || this.pensionAccounts.length === 0;
			if (this.selectedSubSection === 'investments') return !this.investmentAccounts || this.investmentAccounts.length === 0;
			if (this.selectedSubSection === 'loans') return !this.creditInfo || this.creditInfo.length === 0;
			return false;
		},
		showEmptyState() {
			// Show empty state for sub-section filter
			if (this.isCurrentSubSectionEmpty) return true;
			// Show empty state for main section filter
			return (this.selectedSection === 'd2d' && this.isDayToDayEmpty) ||
				(this.selectedSection === 'lt' && this.isLongTermEmpty) ||
				(this.selectedSection === 'ins' && this.isInsuranceEmpty);
		},
		emptyStateHeadline() {
			if (this.selectedSection === 'd2d') return 'Track your everyday spending';
			if (this.selectedSection === 'lt') return 'See your long-term money clearly';
			if (this.selectedSection === 'ins') return 'Keep on top of your cover';
			return '';
		},
		emptyStateSubtext() {
			if (this.selectedSection === 'd2d') return "Work out where your money's going – and where you want it to go instead.";
			if (this.selectedSection === 'lt') return "Get a better view of your future finances – what's growing and where you're headed.";
			if (this.selectedSection === 'ins') return "Store your policies here and we'll remind you before anything's up for renewal.";
			return '';
		},
		emptyStateButtonText() {
			// Sub-section specific text
			if (this.selectedSubSection === 'current') return 'Add current account';
			if (this.selectedSubSection === 'credit') return 'Add credit card';
			if (this.selectedSubSection === 'savings') return 'Add savings account';
			if (this.selectedSubSection === 'mortgages') return 'Add mortgage';
			if (this.selectedSubSection === 'pensions') return 'Add pension';
			if (this.selectedSubSection === 'investments') return 'Add investment';
			if (this.selectedSubSection === 'loans') return 'Add loan';
			// Main section text
			if (this.selectedSection === 'd2d') return 'Add day-to-day account';
			if (this.selectedSection === 'lt') return 'Add long term account';
			if (this.selectedSection === 'ins') return 'Add insurance';
			return '';
		}
	},
	methods: {
		getDayToDayBalance() {
			const total = (this.showCurrentCard ? this.currentAccountsBalance : 0) +
				(this.showCreditCard ? this.creditAccountsBalance : 0) +
				(this.showSavingCard ? this.savingAccountsBalance : 0);
			return this.formatter.format(total);
		},
		getCurrentAccountsBalance() {
			return this.formatter.format(this.currentAccountsBalance);
		},
		getCreditCardsBalance() {
			return this.formatter.format(this.creditAccountsBalance);
		},
		getSavingAccountsBalance() {
			return this.formatter.format(this.savingAccountsBalance);
		},
		getCurrentAccountIcon() {
			return require('@/assets/accountTypes/current-account.png');
		},
		getCreditCardIcon() {
			return require('@/assets/accountTypes/credit-card.png');
		},
		getMortgageIcon() {
			return require('@/assets/accountTypes/mortgage.png');
		},
		getInsuranceIcon() {
			return require('@/assets/accountTypes/insurance.png');
		},
		getCreditInfoIcon() {
			return require('@/assets/accountTypes/loans.png');
		},
		getSavingsAccountIcon() {
			return require('@/assets/accountTypes/savings-account.png');
		},
		getAccountIcons(items, config) {
			const icons = [];
			if (items && items.length > 0) {
				items.forEach(item => {
					const providerPath = config.providerPath.split('.');
					let provider = item;
					for (const key of providerPath) {
						provider = provider?.[key];
					}

					if (provider) {
						// Use logoUri if available, @error handler in AccountCard will handle broken URLs
						let iconUrl = provider.logoUri;

						if (!iconUrl) {
							const typePath = config.typePath.split('.');
							let typeObj = item;
							for (const key of typePath) {
								typeObj = typeObj?.[key];
							}
							let typeName = typeObj?.name || typeObj?.prettyName?.toLowerCase() || config.defaultType;
							if (typeof typeName === 'string') {
								typeName = typeName.toLowerCase().replace(/ /g, '').replace(/&/g, '');
							}
							try {
								iconUrl = require(`@/assets/products/${typeName}.png`);
							} catch (e) {
								// Try provider name as fallback before using default
								try {
									const providerName = provider.name.toLowerCase().replace(/ /g, '').replace(/&/g, '');
									iconUrl = require(`@/assets/products/${providerName}.png`);
								} catch (e2) {
									iconUrl = require('@/assets/round1.png');
								}
							}
						}

						if (iconUrl) {
							icons.push(iconUrl);
						}
					}
				});
			}
			return icons;
		},
		getAllCreditCardIcons() {
			return this.getAccountIcons(this.creditAccounts, {
				providerPath: 'bank',
				typePath: 'accountType',
				defaultType: 'credit'
			});
		},
		getAllPensionAccountIcons() {
			const pensionIcons = this.getAccountIcons(this.pensionAccounts, {
				providerPath: 'provider',
				typePath: 'accountType',
				defaultType: 'pension'
			});
			// Add icons for additional pension documents
			if (this.additionalPensionDocs && this.additionalPensionDocs.length > 0) {
				this.additionalPensionDocs.forEach(doc => {
					pensionIcons.push(this.getDocumentIcon(doc));
				});
			}
			return pensionIcons;
		},
		getAllInvestmentAccountIcons() {
			return this.getAccountIcons(this.investmentAccounts, {
				providerPath: 'provider',
				typePath: 'accountType',
				defaultType: 'investment'
			});
		},
		getAllSavingsAccountIcons() {
			return this.getAccountIcons(this.savingAccounts, {
				providerPath: 'bank',
				typePath: 'accountType',
				defaultType: 'savings'
			});
		},
		getAllCurrentAccountIcons() {
			return this.getAccountIcons(this.currentAccounts, {
				providerPath: 'bank',
				typePath: 'accountType',
				defaultType: 'current'
			});
		},
		getAllMortgageAccountIcons() {
			const mortgageIcons = this.getAccountIcons(this.mortgageInfo, {
				providerPath: 'account.provider',
				typePath: 'policyType',
				defaultType: 'mortgage'
			});
			// Add icons for additional mortgage documents only
			if (this.additionalMortgageDocs && this.additionalMortgageDocs.length > 0) {
				this.additionalMortgageDocs.forEach(doc => {
					mortgageIcons.push(this.getDocumentIcon(doc));
				});
			}
			return mortgageIcons;
		},
		getAllInsuranceAccountIcons() {
			const insuranceIcons = this.getAccountIcons(this.insuranceInfo, {
				providerPath: 'provider',
				typePath: 'policyType',
				defaultType: 'insurance'
			});
			// Add icons for additional insurance documents
			if (this.additionalInsurance && this.additionalInsurance.length > 0) {
				this.additionalInsurance.forEach(doc => {
					insuranceIcons.push(this.getDocumentIcon(doc));
				});
			}
			return insuranceIcons;
		},
		getAllCreditInfoIcons() {
			return this.getAccountIcons(this.creditInfo, {
				providerPath: 'provider',
				typePath: 'creditType',
				defaultType: 'loans'
			});
		},
		handleCurrentAccountClick() {
			this.$emit('filter-changed', { section: 'd2d', subSection: 'current' });
		},
		handleCreditAccountClick() {
			this.$emit('filter-changed', { section: 'd2d', subSection: 'credit' });
		},
		handleSavingAccountClick() {
			this.$emit('filter-changed', { section: 'd2d', subSection: 'savings' });
		},
		handleMortgageClick() {
			this.$emit('filter-changed', { section: 'lt', subSection: 'mortgages' });
		},
		handleCreditClick() {
			this.$emit('filter-changed', { section: 'lt', subSection: 'loans' });
		},
		handleInsuranceClick() {
			this.$emit('filter-changed', { section: 'ins' });
		},
		handlePensionClick() {
			this.$emit('filter-changed', { section: 'lt', subSection: 'pensions' });
		},
		handleInvestmentClick() {
			this.$emit('filter-changed', { section: 'lt', subSection: 'investments' });
		},
		navigateToDayToDay() {
			this.$router.push('/money');
		},
		navigateToLongTerm() {
			this.$router.push('/money');
		},
		navigateToInsurance() {
			this.$router.push('/money');
		},
		handleSpendingAnalysisClick() {
			this.$router.push('/SpendingAnalysis');
		},
		handleAddAccountClick() {
			// Insurance section only has one type, navigate directly
			if (this.selectedSection === 'ins') {
				this.$router.push('/Policies/Insurance/Add');
				return;
			}
			// Sub-sections open the drawer in specific account mode
			const subSections = ['current', 'credit', 'savings', 'mortgages', 'loans', 'pensions', 'investments'];
			if (subSections.includes(this.selectedSubSection)) {
				this.$emit('add-account-click', this.selectedSubSection);
				return;
			}
			// Pass section type to filter the drawer
			this.$emit('add-account-click', this.selectedSection);
		},
		handleAccountListItemClick(account, type) {
			if (type === 'mortgage') {
				this.$router.push(`/Mortgages/${account.id}`);
			} else if (type === 'policy') {
				this.$router.push(`/Policies/${account.id}`);
			} else if (type === 'pension') {
				this.$router.push(`/Pensions/${account.id}`);
			} else if (type === 'investment') {
				this.$router.push(`/Investments/${account.id}`);
			} else if (type === 'loan') {
				this.$router.push(`/Credit/${account.id}`);
			} else {
				// Default for bank accounts, savings, credit cards
				this.$router.push(`/Account/${account.id}`);
			}
		},
		getDocumentIcon(doc) {
			if (doc.sysoDocumentType?.icon) {
				try {
					return require(`@/assets/products/${doc.sysoDocumentType.icon}.png`);
				} catch (e) {
					return require('@/assets/round1.png');
				}
			}
			return require('@/assets/round1.png');
		},
		handleDocumentClick(doc) {
			this.$router.push(`/documents/${doc.id}`);
		},
	}
}
</script>

<style scoped>
.account-cards-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 16px;
	margin-bottom: 16px;
}

.account-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.section-header {
	margin-top: -8px;
	margin-bottom: -4px;
}

.section-header :deep(.title) {
	font-size: 13px;
}

.empty-state {
	text-align: center;
	padding: 0px 0px;
}

.empty-state-text {
	font-size: 18px;
	font-weight: bold;
	margin: 0 0 8px 0;
	color: #413655;
;
}

.empty-state-subtext {
	font-size: 14px;
	color: #5A506E;
	margin: 0 0 24px 0;
}
</style>
